'use no memo';
import { type TransactionLineItem } from '@kalos/kalos-rpc';
import {
  Button,
  DataTable,
  DataTableColumnHeader,
  Input,
  Label,
  SimpleSelect,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import { Pencil1Icon, PlusCircledIcon, PlusIcon, TrashIcon } from '@radix-ui/react-icons';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { type ChangeEvent, useMemo } from 'react';
import { useStore } from 'zustand';

import {
  type DistributionType,
  type ModifiedTransactionLineItem,
} from '../../../../context/TransactionLIneItems';
import { useTransactionLineItemsStore } from '../../../../context/TransactionLIneItems/TransactionLInteItemsStoreContext';
import { useEvent } from '../../../../hooks/useEvent';
import { AmountEditHeader, AmountField } from './EditableFields/Amount';
import { CostCenterEditHeader, CostCenterField } from './EditableFields/CostCenter';
import { DepartmentEditHeader, DepartmentField } from './EditableFields/Department';
import { DescriptionEditHeader, DescriptionField } from './EditableFields/Description';
import { JobEditHeader, JobField } from './EditableFields/Job';
import { SecondaryAmountField } from './EditableFields/SecondaryAmount';
import {
  AdditionalAmountNotesEditHeader,
  SecondaryAmountNote,
} from './EditableFields/SecondaryAmountNote';
import {
  getDistributionAmounts,
  useCancelLineItemEdits,
  useSaveLineItems,
} from './transactionLineItemsActions';

const staticFn = () => undefined;
export const TransactionLineItemsTable = ({
  onSave = staticFn,
  readonly = false,
}: {
  onSave?: () => void;
  readonly?: boolean;
}) => {
  const store = useTransactionLineItemsStore();
  const lineItems = useStore(store, (s) => s.lineItems);
  const addRemainderLineItem = useStore(store, (s) => s.addRemainderLineItem);

  const isEditing = useStore(store, (s) => s.isEditing);

  const onSaveEvent = useEvent(onSave);

  const transactionLineItemsColumnDefs = useMemo<ColumnDef<ModifiedTransactionLineItem>[]>(() => {
    return [
      {
        accessorKey: 'jobId',
        meta: {
          className: 'w-full min-w-[8rem]',
        },
        header(props) {
          return (
            <div className="flex items-center gap-1">
              <DataTableColumnHeader title="Job #" column={props.column} />
              <JobEditHeader />
            </div>
          );
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              <JobField transactionLineItem={props.row.original} />
            </div>
          );
        },
      },
      {
        accessorKey: 'costCenterId',
        meta: {
          className: 'w-40',
        },
        header(props) {
          return (
            <div className="flex items-center gap-1">
              <DataTableColumnHeader title="Purchase Category" column={props.column} />
              <CostCenterEditHeader />
            </div>
          );
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              <CostCenterField transactionLineItem={props.row.original} />
            </div>
          );
        },
      },
      {
        accessorKey: 'departmentId',
        meta: {
          className: 'w-full min-w-[8rem]',
        },
        header(props) {
          return (
            <div className="flex items-center gap-1">
              <DataTableColumnHeader title="Department" column={props.column} />
              <DepartmentEditHeader />
            </div>
          );
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              <DepartmentField transactionLineItem={props.row.original} />
            </div>
          );
        },
      },
      {
        accessorKey: 'description',
        meta: {
          className: 'w-full min-w-[7rem] text-xs',
        },
        header(props) {
          return (
            <div className="flex items-center gap-1">
              <DataTableColumnHeader title="Notes" column={props.column} />
              <DescriptionEditHeader />
            </div>
          );
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              <DescriptionField transactionLineItem={props.row.original} />
            </div>
          );
        },
      },
      {
        accessorKey: 'amount',
        meta: {
          className: 'w-full min-w-[6rem]',
        },
        header(props) {
          return (
            <div className="flex flex-col gap-1">
              <DataTableColumnHeader title="Amount ($)" column={props.column} />
              <AmountEditHeader />
            </div>
          );
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              <AmountField transactionLineItem={props.row.original} />
            </div>
          );
        },
      },
      {
        accessorKey: 'secondary_amount',
        meta: {
          className: 'w-full min-w-[5rem]',
        },
        header(props) {
          return (
            <div className="flex flex-col gap-1">
              <DataTableColumnHeader title="Additional Amount ($)" column={props.column} />
            </div>
          );
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              <SecondaryAmountField transactionLineItem={props.row.original} />
            </div>
          );
        },
      },
      {
        accessorKey: 'secondary_amount_notes',
        meta: {
          className: 'w-full min-w-[7rem] text-xs',
        },
        header(props) {
          return (
            <div className="flex items-center gap-1">
              <DataTableColumnHeader title="Additional Amount Notes" column={props.column} />
              <AdditionalAmountNotesEditHeader />
            </div>
          );
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              <SecondaryAmountNote transactionLineItem={props.row.original} />
            </div>
          );
        },
      },
      ...(!readonly
        ? [
            {
              id: 'actions',
              meta: {
                className: 'flex w-full min-w-[7rem] justify-end',
              },
              header(props) {
                return (
                  <div className={props.column.columnDef.meta?.className}>
                    {!readonly && <ModeToggle onSave={onSaveEvent} />}
                  </div>
                );
              },
              cell(props) {
                return (
                  <div className={props.column.columnDef.meta?.className}>
                    <TooltipProvider>
                      <DeleteAction lineItemId={props.row.original.id} />
                    </TooltipProvider>
                  </div>
                );
              },
            } satisfies ColumnDef<ModifiedTransactionLineItem>,
          ]
        : []),
    ];
  }, [onSaveEvent, readonly]);

  const lineItemsTable = useReactTable({
    data: lineItems,
    columns: transactionLineItemsColumnDefs,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (lineItem) => lineItem.id.toString(),
    enableSorting: false,
  });

  const onAddLineItem = () => {
    addRemainderLineItem();
  };

  return (
    <div className="flex flex-col gap-4">
      <DataTable table={lineItemsTable} />
      {isEditing && (
        <div className="flex py-2">
          <Button onClick={onAddLineItem} className="mx-auto" size="icon-sm">
            <PlusIcon />
          </Button>
        </div>
      )}
    </div>
  );
};

const CancelButton = () => {
  const cancel = useCancelLineItemEdits();

  return (
    <Button onClick={() => void cancel()} size="sm" variant="secondary">
      Cancel
    </Button>
  );
};

const SaveButton = ({ onSave }: { onSave: () => void }) => {
  const { save, isSaving, isDisabled } = useSaveLineItems(onSave);

  return (
    <Button
      onClick={() => void save()}
      isLoading={isSaving}
      disabled={isSaving || isDisabled}
      size="sm"
    >
      Save
    </Button>
  );
};

const AddToAllLineItemsButton = () => {
  const store = useTransactionLineItemsStore();
  const addValueToAllLineItems = useStore(store, (s) => s.addValueToAllLineItems);
  const setEditing = useStore(store, (s) => s.setIsEditing);
  const { amount, distributionType } = useStore(store, (s) => s.amountAndDistribution);
  const setAmountAndDistribution = useStore(store, (s) => s.setAmountAndDistribution);
  const lineItems = useStore(store, (s) => s.lineItems);

  const totalAmount = useStore(store, (s) => s.transaction).amount;
  const amountToApplyList = getDistributionAmounts({
    lineItems,
    amount,
    distributionType,
    totalAmount,
  });

  const onClick = () => {
    addValueToAllLineItems(amountToApplyList);
    setEditing(true);
  };

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const amount = parseFloat(e.target.value) || 0;
    setAmountAndDistribution({ amount: amount, distributionType });
  };

  const handleDistributionChange = (e: DistributionType) => {
    setAmountAndDistribution({ amount, distributionType: e });
  };

  return (
    <div className="space-y-1">
      <Label>Distribution Amount and Type</Label>
      <div className="flex flex-1 flex-col gap-1">
        <Input
          className="flex items-center "
          type="number"
          value={amount}
          onChange={handleAmountChange}
          placeholder="Enter amount"
        />
        <SimpleSelect
          selectedValue={distributionType}
          values={[
            { value: 'even', label: 'Even' },
            { value: 'portioned', label: 'Proportional' },
          ]}
          onChange={handleDistributionChange}
        />
      </div>
      <div>
        <Button
          onClick={onClick}
          className="mt-2 flex items-center gap-1"
          size="sm"
          variant="secondary"
        >
          <span>Add Amount to All Secondary Amounts</span>
          <PlusCircledIcon />
        </Button>
      </div>
    </div>
  );
};

const AddRemainderButton = () => {
  const store = useTransactionLineItemsStore();
  const addRemainderLineItem = useStore(store, (s) => s.addRemainderLineItem);
  const setEditing = useStore(store, (s) => s.setIsEditing);

  const onClick = () => {
    addRemainderLineItem();
    setEditing(true);
  };

  return (
    <Button onClick={onClick} className="flex items-center gap-1" size="sm" variant="secondary">
      <span>Add Remainder</span>
      <PlusCircledIcon />
    </Button>
  );
};

const ModeToggle = ({ onSave }: { onSave: () => void }) => {
  const store = useTransactionLineItemsStore();
  const isEditing = useStore(store, (s) => s.isEditing);
  const setIsEditing = useStore(store, (s) => s.setIsEditing);
  const lineItemLength = useStore(store, (s) => s.lineItems.length);
  const isAmountValid = useStore(store, (s) => s.isAmountValid());

  return (
    <div className="flex flex-wrap items-center justify-end gap-1 py-2 md:py-4">
      {isAmountValid === false && lineItemLength > 0 && <AddToAllLineItemsButton />}
      {isAmountValid === false && <AddRemainderButton />}
      {isEditing ? (
        <>
          <CancelButton />
          <SaveButton onSave={onSave} />
        </>
      ) : (
        <Button size="icon" onClick={() => setIsEditing(true)}>
          <Pencil1Icon />
        </Button>
      )}
    </div>
  );
};

const DeleteAction = ({ lineItemId }: { lineItemId: TransactionLineItem['id'] }) => {
  const store = useTransactionLineItemsStore();
  const isEditing = useStore(store, (s) => s.isEditing);
  const setLineItems = useStore(store, (s) => s.setLineItems);

  const onDeleteClick = () => {
    setLineItems((prevLineItems) => prevLineItems.filter((li) => li.id !== lineItemId));
  };

  return (
    <Tooltip>
      <TooltipContent>{isEditing ? 'Delete' : 'Please start editing to delete'}</TooltipContent>
      <TooltipTrigger asChild>
        <span>
          <Button size="icon" onClick={onDeleteClick} disabled={!isEditing} variant="secondary">
            <TrashIcon />
          </Button>
        </span>
      </TooltipTrigger>
    </Tooltip>
  );
};
